// =============================================================================
// api/googleReviews.ts — typed client for the booking-google-reviews function
// =============================================================================
// Google reviews on neoseum.com without copying them by hand. The function
// reads every venue's Google reviews at night, lets Jev (a decision model:
// positive? quotable? which expedition? which language?) sort them, and
// publishes the ones that pass the gates as Webflow "Reviews" items. Until
// auto_publish is switched on, nothing is published without a person: the
// grey zone and everything above it waits in the queue (status 'suggested').
//
// Same action-routed shape and the same operator JWT + booking_marketing
// permission as the follow-up mails.
import { BOOKING_API_URL, supabase } from '../lib/supabase';

export type GoogleReviewStatus = 'new' | 'suggested' | 'approved' | 'published' | 'skipped' | 'rejected';
export const GOOGLE_REVIEW_STATUSES: GoogleReviewStatus[] = [
  'new',
  'suggested',
  'approved',
  'published',
  'skipped',
  'rejected',
];

export type GoogleReviewLanguage = 'en' | 'nl' | 'other';

export interface GoogleReviewSettings {
  // false = every review that passes the gates waits in the queue; true = it
  // is published on the spot.
  auto_publish: boolean;
  min_stars: number;
  min_comment_chars: number;
  // Jev probabilities, 0..1.
  min_positive: number;
  min_publishable: number;
  suggest_floor: number;
  min_expedition_confidence: number;
  classify_per_tick: number;
}

export interface GoogleRating {
  location_id: string;
  rating: number | null;
  review_count: number | null;
  source: 'places' | 'business_profile';
  fetched_at: string;
  webflow_item_id: string | null;
}

export interface GoogleReviewLocation {
  id: string;
  name: string;
  display_name: string;
  slug: string | null;
  google_place_id: string | null;
  google_business_location: string | null;
  rating: GoogleRating | null;
  counts: Partial<Record<GoogleReviewStatus, number>>;
}

export interface GoogleReviewExpedition {
  key: string;
  name: string;
  cues: string;
  item_id: string;
}

export interface GoogleReviewsConfigured {
  places: boolean;
  business_profile: boolean;
  jev: boolean;
  webflow: boolean;
}

export interface GoogleReviewsOverview {
  settings: GoogleReviewSettings;
  locations: GoogleReviewLocation[];
  expeditions: GoogleReviewExpedition[];
  configured: GoogleReviewsConfigured;
  webflow_error: string | null;
  queue_count: number;
}

export interface GoogleReviewRow {
  review_id: string;
  location_id: string;
  google_name: string | null;
  star_rating: number;
  comment: string | null;
  language: GoogleReviewLanguage | null;
  reviewer_name: string | null;
  reviewer_photo_url: string | null;
  reviewer_anonymous: boolean;
  google_created_at: string;
  google_updated_at: string | null;
  reply_comment: string | null;
  reply_at: string | null;
  classified_at: string | null;
  jev: unknown;
  positive: number | null;
  publishable: number | null;
  expedition_key: string | null;
  expedition_confidence: number | null;
  status: GoogleReviewStatus;
  skip_reason: string | null;
  published_expedition_key: string | null;
  decided_by: string | null;
  decided_at: string | null;
  webflow_item_id: string | null;
  published_at: string | null;
  error: string | null;
  created_at: string;
  updated_at: string;
}

export interface GoogleReviewsList {
  reviews: GoogleReviewRow[];
  total: number;
  limit: number;
  offset: number;
}

export interface GoogleReviewsClassifyReport {
  considered: number;
  jev_calls: number;
  suggested: number;
  approved: number;
  skipped: number;
  failed: number;
}

export interface GoogleReviewsRunReport {
  dry_run: boolean;
  configured: GoogleReviewsConfigured;
  ratings: Array<{
    location: string;
    source: string;
    rating: number | null;
    review_count: number | null;
    error?: string;
  }>;
  imports: Array<{
    location: string;
    pages: number;
    seen: number;
    inserted: number;
    updated: number;
    error?: string;
  }>;
  classify: GoogleReviewsClassifyReport | null;
  publish: { published: number; failed: number } | null;
  rating_items: Array<{ slug: string; created: boolean; changed: boolean; published: boolean; error?: string }>;
  errors: string[];
  took_ms: number;
}

export interface GoogleReviewsError {
  error: string;
  message?: string;
}

export interface GoogleReviewsResult<T> {
  ok: boolean;
  data: T | null;
  error: GoogleReviewsError | null;
  status: number;
}

async function call<T>(
  action: string,
  body: Record<string, unknown> = {},
): Promise<GoogleReviewsResult<T>> {
  const { data: sessionData } = await supabase.auth.getSession();
  const jwt = sessionData.session?.access_token;
  if (!jwt) {
    return {
      ok: false,
      data: null,
      error: { error: 'unauthenticated', message: 'No active session' },
      status: 401,
    };
  }

  let res: Response;
  try {
    res = await fetch(`${BOOKING_API_URL}/booking-google-reviews`, {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
        Authorization: `Bearer ${jwt}`,
      },
      body: JSON.stringify({ action, ...body }),
    });
  } catch (e) {
    return {
      ok: false,
      data: null,
      error: { error: 'network_error', message: e instanceof Error ? e.message : String(e) },
      status: 0,
    };
  }

  let json: unknown = null;
  try {
    json = await res.json();
  } catch {
    /* empty / non-JSON */
  }

  if (!res.ok) {
    const err = (json ?? {}) as Partial<GoogleReviewsError>;
    return {
      ok: false,
      data: null,
      error: { error: err.error ?? `http_${res.status}`, message: err.message },
      status: res.status,
    };
  }
  return { ok: true, data: json as T, error: null, status: res.status };
}

export function getGoogleReviewsOverview() {
  return call<GoogleReviewsOverview>('overview');
}

export function listGoogleReviews(filter: {
  status?: GoogleReviewStatus | null;
  location_id?: string | null;
  limit?: number;
  offset?: number;
}) {
  return call<GoogleReviewsList>('list', filter);
}

export function saveGoogleReviewSettings(settings: GoogleReviewSettings) {
  return call<{ settings: GoogleReviewSettings }>('save_settings', { settings });
}

export function saveGoogleReviewLocation(input: {
  location_id: string;
  google_place_id: string;
  google_business_location: string;
}) {
  return call<{ locations: GoogleReviewLocation[] }>('save_location', input);
}

// expedition_key: undefined = keep Jev's pick, '' = none, otherwise the slug.
export function decideGoogleReview(
  review_id: string,
  decision: 'publish' | 'reject',
  expedition_key?: string,
) {
  return call<{
    review: GoogleReviewRow | null;
    publish: { published: number; failed: number; items: Array<{ review_id: string; error?: string }> } | null;
    webflow_configured: boolean;
  }>('decide', { review_id, decision, ...(expedition_key === undefined ? {} : { expedition_key }) });
}

// One review (any non-final status, re-run) or every 'new' one.
export function classifyGoogleReviews(review_id?: string) {
  return call<{ classify: GoogleReviewsClassifyReport }>('classify', review_id ? { review_id } : {});
}

export function runGoogleReviews(dry_run: boolean) {
  return call<GoogleReviewsRunReport>('run', { dry_run });
}
